import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { EditSubStatusComponent } from 'src/app/components/edit-sub-status/edit-sub-status.component';
import { CONVERSATION_STATUS, STATUS_COLUMN_FORMAT_MAP } from 'src/app/constants';
import { SubStatus } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SubStatusesService } from 'src/services/sub-statuses.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, SharedPipesModule],
  selector: 'app-sub-statuses-list',
  templateUrl: './sub-statuses-list.component.html',
  styleUrls: ['./sub-statuses-list.component.scss'],
})
export class SubStatusesListComponent implements OnInit, OnDestroy {
  subStatuses: Partial<SubStatus>[] = [];
  filteredSubStatuses: Partial<SubStatus>[] = [];
  groupedSubStatuses: { [key: string]: Partial<SubStatus>[] } = {};

  searchText: string;
  statusTypeFilter: string = null;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  routerSub: Subscription;
  projectId: string;
  statusTypes = Object.values(CONVERSATION_STATUS);
  typeLabels = Object.keys(CONVERSATION_STATUS).reduce((acc, key) => {
    acc[CONVERSATION_STATUS[key]] = key.replace(/_/g, ' ').toLowerCase();
    return acc;
  }, {});
  STATUS_COLUMN_FORMAT_MAP = STATUS_COLUMN_FORMAT_MAP;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private subStatusesService: SubStatusesService,
    private msgSrvc: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private alertCtrl: AlertController,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  async ngOnInit() {
    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        this.isLoading = true;

        await Promise.all([this.getSubStatuses()]);

        this.isLoading = false;
      });

    this.route.queryParams.subscribe(async (params) => {
      if (params.id) {
        const modal = await this.modalCtrl.create({
          component: EditSubStatusComponent,
          componentProps: {
            id: params.id,
            projectId: this.projectId,
          },
          cssClass: 'modal-short',
        });

        modal.present();

        modal.onDidDismiss().then(() => {
          this.navCtrl.navigateForward(this.router.url, {
            queryParams: {},
          });
          this.getSubStatuses();
        });
      }
    });
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  async getSubStatuses() {
    try {
      const subStatusesResponse = await this.subStatusesService.list({
        limit: null,
        order: ['type', 'position'],
        descending: false,
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.projectId,
          },
        ],
      });

      this.subStatuses = subStatusesResponse.data;
      this.handleFilters();
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  showNextPage() {
    this.pageCount += 1;
  }

  handleFilters() {
    let tempFiltered = this.subStatuses;

    const term = this.searchText?.trim().toLowerCase();

    if (term) {
      tempFiltered = this.subStatuses.filter(
        (subStatus) =>
          subStatus.name.toLowerCase().includes(term) ||
          (this.typeLabels[subStatus.type] || '').toLowerCase().includes(term),
      );
    }

    if (this.statusTypeFilter) {
      tempFiltered = tempFiltered.filter((subStatus) => subStatus.type === this.statusTypeFilter);
    }

    this.filteredSubStatuses = tempFiltered;

    this.groupedSubStatuses = tempFiltered.reduce((acc: { [key: string]: Partial<SubStatus>[] }, subStatus) => {
      const type = subStatus.type;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(subStatus);
      acc[type].sort((a, b) => a.position - b.position);
      return acc;
    }, {});
  }

  async createSubStatus(type?: string) {
    const modal = await this.modalCtrl.create({
      component: EditSubStatusComponent,
      componentProps: {
        projectId: this.projectId,
        type,
      },
      cssClass: 'modal-short',
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getSubStatuses();
    });
  }

  async editSubStatus(id: string) {
    this.navCtrl.navigateForward(this.router.url, {
      queryParams: {
        id,
        projectId: this.projectId,
      },
    });
  }

  async moveUp(row: SubStatus) {
    if (row.position <= 0) {
      return;
    }

    try {
      await this.subStatusesService.updatePartial(row.id, { position: row.position - 1 });
      await this.getSubStatuses();
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async moveDown(row: SubStatus) {
    const sameTypeRows = this.subStatuses.filter((s) => s.type === row.type);
    const maxPosition = sameTypeRows.length - 1;

    if (row.position >= maxPosition) {
      return;
    }

    try {
      await this.subStatusesService.updatePartial(row.id, { position: row.position + 1 });
      await this.getSubStatuses();
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async deleteSubStatus(row: SubStatus) {
    try {
      const alert = await this.alertCtrl.create({
        header: 'Delete Status',
        message: 'Are you sure you want to delete this status? This action cannot be undone.',
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Delete',
            role: 'destructive',
            handler: async () => {
              this.isLoading = true;
              await this.subStatusesService.delete(row.id);
              this.modalCtrl.dismiss();

              await this.getSubStatuses();

              this.isLoading = false;
            },
          },
        ],
      });

      await alert.present();
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }
}
